import { useModals, Box, Stack } from "@skailer-com/uikit";
import { getHSLAColor } from "@/helpers/style.helper.ts";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import { PreviewEmployeesModal } from "@/pages/employees/employee/widgets/PreviewEmployeesModal.tsx";
import { AnalyticReportTypes } from "@/types/analytics/analytics.types.ts";
import { ApiSurveyChart } from "@/types/survey.types.ts";
import {
  BarController,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  type ChartData,
  ChartDataset,
  type ChartOptions,
  Legend,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Title as TitleChart,
  Tooltip,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { useEffect, useMemo, useRef } from "react";
import { Chart, getElementAtEvent } from "react-chartjs-2";
import { useTranslation } from "react-i18next";

export const SurveySummaryChart = ({ surveyChart }: { surveyChart: ApiSurveyChart }) => {
  const { t } = useTranslation();
  const chartRef = useRef<ChartJS | null>(null);
  const { formatPeriodDates } = useFormatter();
  const { openModal } = useModals();

  ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    TitleChart,
    Tooltip,
    Legend,
    LineController,
    BarController,
    ChartDataLabels
  );

  const chartVM = useMemo(() => {
    const completed: ChartDataset = {
      type: "line",
      data: [],
      borderColor: "#000",
      borderWidth: 2,
      pointBorderWidth: 3,
      backgroundColor: "white",
      borderJoinStyle: "bevel",
      pointRadius: 8,
      pointHoverRadius: 8,
      pointHoverBorderWidth: 3,
      pointHitRadius: 20,
      pointStyle: "circle",
      tension: 0.5,
      fill: false,
      yAxisID: "y",
    };
    const labels: string[] = [];

    for (const row of surveyChart.completed_by_periods) {
      const title =
        formatPeriodDates({
          dateStart: row.date_period_start,
          dateEnd: row.date_period_end,
        })?.title || "";

      labels.push(title);
      completed.data.push(row.total_count);
    }

    return { labels, datasets: [completed] } as ChartData;
  }, [surveyChart]);

  useEffect(() => {
    const handleBeforePrint = () => {
      if (chartRef.current) {
        chartRef.current.resize(750, chartRef.current.height);
      }
    };

    const handleAfterPrint = () => {
      if (chartRef.current) {
        chartRef.current.resize();
      }
    };

    window.addEventListener("beforeprint", handleBeforePrint);
    window.addEventListener("afterprint", handleAfterPrint);

    return () => {
      window.removeEventListener("beforeprint", handleBeforePrint);
      window.removeEventListener("afterprint", handleAfterPrint);
    };
  }, []);

  const totalCompletedMax = Math.max(
    ...surveyChart.completed_by_periods.map((row) => row.total_count)
  );
  const totalCompletedMaxWithBounds = Math.ceil(totalCompletedMax * 1.1);

  const options: ChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      datalabels: {
        anchor: "end",
        align: "top",
        offset: 5,
        color: getHSLAColor("--color-brand-dark", 0.6),
        display: false,
        clamp: false,
        clip: false,
      },
      legend: {
        display: false,
      },
      title: {
        display: false,
      },

      tooltip: {
        displayColors: false,
        callbacks: {
          label: function (context) {
            const period_count = context.parsed.y ?? 0;

            const period_rate =
              surveyChart.total_participants > 0
                ? ((period_count / surveyChart.total_participants) * 100).toFixed() + "%"
                : "";

            return [
              t("surveys:participant_completed_label", {
                count: period_count,
              }),
              period_rate,
            ];
          },
        },
      },
    },
    hover: {
      intersect: false,
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
      },
      y: {
        type: "linear",
        display: true,
        position: "right",
        max: totalCompletedMaxWithBounds,
        min: 0,
        border: {
          display: false,
        },

        grid: {
          color: function (context) {
            if (context.tick.value >= 0) {
              return getHSLAColor("--color-brand-dark", 0.1);
            } else if (context.tick.value < 0) {
              return "transparent";
            }
          },
        },
        ticks: {
          display: false,
        },
      },
    },
  };

  const handleClick = (event: React.MouseEvent<HTMLCanvasElement>) => {
    if (!chartRef.current) return;
    const element = getElementAtEvent(chartRef.current, event);

    if (element.length > 0) {
      const { index } = element[0];

      const periodData = surveyChart.completed_by_periods[index];
      if (periodData.total_employee_ids.length === 0) return;

      openModal(PreviewEmployeesModal, {
        filter: {
          page_number: 1,
          employee_ids: periodData.total_employee_ids,
        },
        addColumns: [],
        modalTitle: t("reporting:joiners"),
        source: AnalyticReportTypes.surveySummaryChart,
      });
    }
  };

  return (
    <Stack gap={"lg"}>
      <Box className={"h-[240px]"}>
        <Chart
          ref={chartRef}
          onClick={handleClick}
          type={"line"}
          plugins={[ChartDataLabels]}
          options={options}
          data={chartVM}
        />
      </Box>
    </Stack>
  );
};
